// Always-visible scrollbar + click-and-drag scrolling for a featured-collection
// slider using the `scrollbar` nav style. The native scrollbar auto-hides on
// macOS and touch devices, so the thumb here mirrors the slider's scroll
// position and can be dragged or clicked. Touch scrolling stays native.
if (!customElements.get('slider-scrollbar')) {
  class SliderScrollbar extends HTMLElement {
    connectedCallback() {
      this.slider = this.parentElement && this.parentElement.querySelector('.slider--show-scrollbar');
      this.thumb = this.querySelector('.slider-scrollbar__thumb');
      if (!this.slider || !this.thumb) return;

      this.sync = this.sync.bind(this);
      this.slider.addEventListener('scroll', this.sync, { passive: true });
      new ResizeObserver(this.sync).observe(this.slider);
      new ResizeObserver(this.sync).observe(this);

      // The whole track (including its taller invisible hit area) is one drag handle.
      this.addEventListener('pointerdown', (event) => this.startDrag(event));

      this.initContentDrag();
      this.sync();
    }

    maxScroll() {
      return this.slider.scrollWidth - this.slider.clientWidth;
    }

    // Maps a viewport x (left edge of the thumb) to a slider scrollLeft.
    scrollForTrackX(x) {
      const room = this.clientWidth - this.thumb.offsetWidth;
      if (room <= 0) return 0;
      const ratio = Math.min(Math.max((x - this.getBoundingClientRect().left) / room, 0), 1);
      return ratio * this.maxScroll();
    }

    sync() {
      const max = this.maxScroll();
      this.hidden = max <= 1;
      if (this.hidden) return;
      const width = Math.max((this.slider.clientWidth / this.slider.scrollWidth) * this.clientWidth, 40);
      this.thumb.style.width = `${width}px`;
      this.thumb.style.transform = `translateX(${(this.slider.scrollLeft / max) * (this.clientWidth - width)}px)`;
    }

    // Grabbing the thumb keeps its position under the pointer; grabbing anywhere
    // else on the track centers the thumb there first, then keeps dragging.
    startDrag(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      const offset =
        event.target === this.thumb
          ? event.clientX - this.thumb.getBoundingClientRect().left
          : this.thumb.offsetWidth / 2;
      this.classList.add('is-dragging');
      this.slider.classList.add('is-dragging');
      this.setPointerCapture(event.pointerId);
      const move = (e) => {
        this.slider.scrollLeft = this.scrollForTrackX(e.clientX - offset);
      };
      const end = () => {
        this.removeEventListener('pointermove', move);
        this.removeEventListener('pointerup', end);
        this.removeEventListener('pointercancel', end);
        this.classList.remove('is-dragging');
        this.slider.classList.remove('is-dragging');
      };
      this.addEventListener('pointermove', move);
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
      move(event);
    }

    // Mouse-only drag on the slider itself; a small threshold keeps plain
    // clicks on cards working, and a drag swallows the click that follows.
    initContentDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let pressed = false;

      this.slider.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pressed = true;
        dragging = false;
        startX = event.clientX;
        startScroll = this.slider.scrollLeft;
      });

      window.addEventListener('pointermove', (event) => {
        if (!pressed) return;
        const delta = event.clientX - startX;
        if (!dragging && Math.abs(delta) < 5) return;
        if (!dragging) {
          dragging = true;
          this.slider.classList.add('is-dragging');
        }
        this.slider.scrollLeft = startScroll - delta;
      });

      const end = () => {
        if (!pressed) return;
        pressed = false;
        this.slider.classList.remove('is-dragging');
      };
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);

      this.slider.addEventListener(
        'click',
        (event) => {
          if (!dragging) return;
          event.preventDefault();
          event.stopPropagation();
          dragging = false;
        },
        true
      );

      // Stop the browser's native image/link ghost-drag from hijacking the gesture.
      this.slider.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  customElements.define('slider-scrollbar', SliderScrollbar);
}
